import { useEffect, useRef, useState } from "react";
import ProfileImg from "../../src/assets/images/logo.png";

const apiUrl = process.env.REACT_APP_API_URL;

const handleLogout = () => {
  // Redirect to the backend logout route
  window.location.href = `${apiUrl}logout`;
};

const handleLogoutRedirect = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const confirmation = urlParams.get("confirmation");
  if (confirmation === "logout_success") {
    // Clear the access token from local storage
    localStorage.removeItem("accessToken");

    // Redirect to the dashboard
    window.location.href = "/";
  }
};

// Clear zipcodes on page load (outside of the component to ensure it runs on each page reload)
const clearSearchParamsOnLoad = () => {
  console.log("Clearing zipcodes from localStorage on page load");
  localStorage.removeItem("currentZipcodes");
  localStorage.removeItem("currentFilterCarrier");
  localStorage.removeItem("currentFilterControlNo");
  localStorage.removeItem("currentLatitude");
  localStorage.removeItem("currentLongitude");
  localStorage.removeItem("radius");
  localStorage.removeItem("isControlNoOnlySearch");
  localStorage.removeItem("specificControlNoRecord");
  localStorage.removeItem("currentFilterStatus");
  localStorage.removeItem("currentExcludeControlNos");
};

// Run on window load
window.onload = () => {
  clearSearchParamsOnLoad();
  handleLogoutRedirect();
};

const GcSidebar = ({ toggleSidebar }) => {
  const [userName, setUserName] = useState("");
  const [carriers, setCarriers] = useState([]);
  const [grandTotals, setGrandTotals] = useState({
    totalTIV: "0.00",
    totalPremium: "0.00",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasData, setHasData] = useState(false);
  const [mainComponentLoading, setMainComponentLoading] = useState(false);
  const [zipcodesLastUpdated, setZipcodesLastUpdated] = useState(null);
  const [activeFilterCarrier, setActiveFilterCarrier] = useState("");
  const [activeFilterControlNo, setActiveFilterControlNo] = useState("");

  const isMobile = () => window.innerWidth <= 768;
  const [isExpanded, setIsExpanded] = useState(!isMobile());
  const [hover, setHover] = useState(false);
  const apiCallInProgress = useRef(false);
  const lastFetchedZipcodes = useRef(null);
  // Track the in-flight carrier-breakdown request so a new fetch cancels the old one cleanly — and so we know to ignore the resulting
  // AbortError instead of surfacing "signal is aborted without reason" to the UI.
  const carrierAbortController = useRef(null);
  const [activeFilterHasClaimsOnly, setActiveFilterHasClaimsOnly] =
    useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsExpanded(!isMobile());
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user && user.name) {
      setUserName(user.name);
    }

    localStorage.removeItem("currentZipcodes");
  }, []);

  useEffect(() => {
    function logStorageChange(e) {
      if (
        e.key === "currentFilterCarrier" ||
        e.key === "currentFilterStatus" ||
        e.key === "currentFilterControlNo" ||
        e.key === "currentLatitude" ||
        e.key === "currentLongitude" ||
        e.key === "radius"
      ) {
        console.log(`Storage changed - ${e.key}: ${e.newValue}`);
      }
    }

    window.addEventListener("storage", logStorageChange);
    return () => window.removeEventListener("storage", logStorageChange);
  }, []);

  useEffect(() => {
    const handleFilterParamsChanged = (event) => {
      console.log("Filter params changed:", event.detail);
      const { carrier, controlNo, hasClaimsOnly } = event.detail;

      setActiveFilterCarrier(carrier || "");
      setActiveFilterControlNo(controlNo || "");
      setActiveFilterHasClaimsOnly(hasClaimsOnly || false); // Add this line
    };

    window.addEventListener("filter-params-changed", handleFilterParamsChanged);

    // Get initial values from localStorage
    const savedCarrier = localStorage.getItem("currentFilterCarrier");
    const savedControlNo = localStorage.getItem("currentFilterControlNo");
    const savedStatus = localStorage.getItem("currentFilterStatus");
    const savedHasClaimsOnly =
      localStorage.getItem("currentFilterHasClaimsOnly") === "true"; // Add this line

    if (savedCarrier) {
      setActiveFilterCarrier(savedCarrier);
    }

    if (savedControlNo) {
      setActiveFilterControlNo(savedControlNo);
    }

    if (savedHasClaimsOnly) {
      setActiveFilterHasClaimsOnly(savedHasClaimsOnly); // Add this line
    }

    return () => {
      window.removeEventListener(
        "filter-params-changed",
        handleFilterParamsChanged
      );
    };
  }, []);

  // Enhanced fetch function with retry logic and timeout handling.
  // Honor an external AbortSignal so the caller can cancel all retries at once. Propagate aborts immediately instead of retrying, and
  // distinguish external cancel vs local timeout so the UI can react correctly.
  const fetchWithRetry = async (
    url,
    options,
    maxRetries = 3,
    baseDelay = 2000,
    externalSignal = null
  ) => {
    let retries = 0;
    let lastError;

    while (retries < maxRetries) {
      // Bail before even starting a new attempt if the caller has cancelled.
      if (externalSignal?.aborted) {
        const err = new DOMException("Request cancelled", "AbortError");
        throw err;
      }

      try {
        // 30s local timeout per attempt. Merge with any external signal so
        // either source triggers abort.
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000);
        const onExternalAbort = () => controller.abort();
        if (externalSignal) {
          externalSignal.addEventListener("abort", onExternalAbort, {
            once: true,
          });
        }

        const updatedOptions = {
          ...options,
          signal: controller.signal,
        };

        try {
          const response = await fetch(url, updatedOptions);
          if (!response.ok) {
            throw new Error(`API request failed with status ${response.status}`);
          }
          return await response.json();
        } finally {
          clearTimeout(timeoutId);
          if (externalSignal) {
            externalSignal.removeEventListener("abort", onExternalAbort);
          }
        }
      } catch (error) {
        lastError = error;

        // External cancel (new search started, unmount): stop immediately.
        if (externalSignal?.aborted) {
          throw error;
        }

        if (error.name === "AbortError") {
          console.warn(
            `API call timed out, attempt ${retries + 1} of ${maxRetries}`
          );
        } else {
          console.error(
            `API call failed, attempt ${retries + 1} of ${maxRetries}:`,
            error
          );
        }

        const delay = baseDelay * Math.pow(2, retries);
        await new Promise((resolve) => setTimeout(resolve, delay));
        retries++;
      }
    }

    throw lastError;
  };

  const fetchCarrierBreakdown = async (forceFetch = false) => {
    // Skip if already in progress
    if (apiCallInProgress.current && !forceFetch) return;

    // Cancel any in-flight carrier-breakdown request before
    // starting a new one so stale responses don't clobber fresh state — and
    // so the retry loop in fetchWithRetry stops retrying immediately.
    if (carrierAbortController.current) {
      carrierAbortController.current.abort();
    }
    const controller = new AbortController();
    carrierAbortController.current = controller;

    try {
      apiCallInProgress.current = true;
      setLoading(true);

      // Always get fresh data from localStorage
      const zipcodesData = localStorage.getItem("currentZipcodes");
      if (!zipcodesData) {
        // Allow fetch if carrier filter is present even if zipcodes missing
        const carrier = localStorage.getItem("currentFilterCarrier") || "";
        if (!carrier) {
          setCarriers([]);
          setGrandTotals({ totalTIV: "0.00", totalPremium: "0.00" });
          setHasData(false);
          return;
        }
      }

      // Get fresh filter values directly from localStorage
      const carrier = localStorage.getItem("currentFilterCarrier") || "";
      const controlNo = localStorage.getItem("currentFilterControlNo") || "";
      const lat = localStorage.getItem("currentLatitude") || "";
      const lng = localStorage.getItem("currentLongitude") || "";
      const radius = localStorage.getItem("radius") || "";
      const isControlNoOnlySearch =
        localStorage.getItem("isControlNoOnlySearch") === "true";
      const status = localStorage.getItem("currentFilterStatus") || "Bound";
      const hasClaimsOnly =
        localStorage.getItem("currentFilterHasClaimsOnly") === "true"; // Add this line

      console.log("Fresh values from localStorage:", {
        carrier,
        controlNo,
        lat,
        lng,
        radius,
        hasLat: Boolean(lat && lat !== "null" && lat.trim() !== ""),
        hasLng: Boolean(lng && lng !== "null" && lng.trim() !== ""),
        isControlNoOnlySearch,
        status,
        hasClaimsOnly, // Add this line
      });

      // Parse zipcodes
      let zipcodes;
      try {
        const parsedZipcodes = JSON.parse(zipcodesData);
        zipcodes = Array.isArray(parsedZipcodes)
          ? parsedZipcodes.join(",")
          : zipcodesData;
      } catch (e) {
        zipcodes = zipcodesData;
      }

      // Build URL manually
      let url = `${apiUrl}fetch-carriers-summary?zipcodes=${zipcodes}`;

      // Add carrier if present
      if (carrier && carrier.trim() !== "") {
        url += `&carrierName=${encodeURIComponent(carrier)}`;
        console.log("Added carrier to URL:", carrier);
      }

      // Only add controlNo when not control-only search
      if (!isControlNoOnlySearch) {
        url += `&controlNo=${encodeURIComponent(controlNo)}`;
        console.log("Added controlNo to URL:", controlNo);
      }

      if (status) {
        url += `&status=${encodeURIComponent(status)}`;
        console.log("Added status to URL:", status);
      }

      // Add hasClaimsOnly filter if active
      if (hasClaimsOnly) {
        url += `&hasClaimsOnly=true`;
        console.log("Added hasClaimsOnly to URL:", hasClaimsOnly);
      }

      // (carrier breakdown precision): the main component
      // computes the list of geocoded ControlNos that fall outside the radius
      // and stashes them here. The backend applies NOT IN, so the sidebar
      // totals stop counting records the map/list have already trimmed.
      const excludeControlNos =
        localStorage.getItem("currentExcludeControlNos") || "";
      if (excludeControlNos) {
        url += `&excludeControlNos=${encodeURIComponent(excludeControlNos)}`;
        console.log(
          "Added excludeControlNos to URL:",
          excludeControlNos.split(",").length,
          "ids"
        );
      }

      console.log("Final URL:", url);

      const token = localStorage.getItem("accessToken");
      const data = await fetchWithRetry(
        url,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
        3,
        2000,
        controller.signal
      );

      // If a newer fetch superseded this one, discard the stale payload.
      if (controller.signal.aborted) return;

      setError(null);
      setCarriers(data.data || []);
      setGrandTotals(
        data.meta?.grandTotals || { totalTIV: "0.00", totalPremium: "0.00" }
      );
      setHasData((data.data || []).length > 0);
      setZipcodesLastUpdated(new Date());
    } catch (err) {
      // An abort is an expected outcome when the user changes filters mid-flight — don't treat it as a user-facing error.
      const isAbort =
        err?.name === "AbortError" ||
        controller.signal.aborted ||
        (typeof err?.message === "string" &&
          err.message.toLowerCase().includes("aborted"));
      if (isAbort) {
        console.log("Carrier breakdown fetch cancelled; ignoring abort error");
        return;
      }
      console.error("Error:", err);
      setError(err.message);
      setHasData(false);
    } finally {
      // Only release the in-progress flag if this is still the current request.
      if (carrierAbortController.current === controller) {
        carrierAbortController.current = null;
        apiCallInProgress.current = false;
        setLoading(false);
      }
    }
  };

  // Add event listeners for better coordination with the main component
  useEffect(() => {
    // Listen for events from the GeographicalConcentration component
    const handleDataLoadingStart = () => {
      console.log("Received geo-data-loading-start event");
      setMainComponentLoading(true);
    };

    const handleDataLoadingComplete = (event) => {
      console.log("Received geo-data-loading-complete event", event.detail);
      // Add a small delay to allow loader to be visible
      setTimeout(() => setMainComponentLoading(false), 300);

      // If filters were provided in the event, update them
      if (event.detail && event.detail.filters) {
        const { carrier, controlNo, hasClaimsOnly } = event.detail.filters;
        setActiveFilterCarrier(carrier || "");
        setActiveFilterControlNo(controlNo || "");
        setActiveFilterHasClaimsOnly(hasClaimsOnly || false); // Add this line
      }

      // Record when zipcodes were last updated
      setZipcodesLastUpdated(new Date());

      // Wait a bit to ensure all data is ready, then force fetch
      setTimeout(() => {
        fetchCarrierBreakdown(true);
      }, 1000);
    };

    const handleDataLoadingFailed = () => {
      console.log("Received geo-data-loading-failed event");
      setMainComponentLoading(false);
      // Reset our state
      setCarriers([]);
      setGrandTotals({
        totalTIV: "0.00",
        totalPremium: "0.00",
      });
      setHasData(false);
    };

    const handleDataClearing = () => {
      console.log("Received geo-data-clearing event");
      setMainComponentLoading(true);
    };

    const handleDataCleared = () => {
      console.log("Received geo-data-cleared event");
      setMainComponentLoading(false);
      // Reset our state
      setCarriers([]);
      setGrandTotals({
        totalTIV: "0.00",
        totalPremium: "0.00",
      });
      setHasData(false);
      setZipcodesLastUpdated(null);
      lastFetchedZipcodes.current = null;

      // Also clear filter states
      setActiveFilterCarrier("");
      setActiveFilterControlNo("");
      setActiveFilterHasClaimsOnly(false); // Add this line
    };

    const handleMarkersLoaded = (event) => {
      console.log("All markers loaded event received", event.detail);

      // IMPORTANT: Always get fresh values from localStorage here
      const freshCarrier = localStorage.getItem("currentFilterCarrier") || "";
      const freshControlNo =
        localStorage.getItem("currentFilterControlNo") || "";
      const freshLat = localStorage.getItem("currentLatitude") || "";
      const freshLng = localStorage.getItem("currentLongitude") || "";
      const freshRadius = localStorage.getItem("radius") || "";
      const isControlNoOnlySearch =
        localStorage.getItem("isControlNoOnlySearch") === "true";
      const status = localStorage.getItem("currentFilterStatus") || "";
      const hasClaimsOnly =
        localStorage.getItem("currentFilterHasClaimsOnly") === "true"; // Add this line

      // Log the fresh values for debugging
      console.log("Fresh localStorage values at markers loaded:", {
        carrier: freshCarrier,
        controlNo: freshControlNo,
        lat: freshLat,
        lng: freshLng,
        radius: freshRadius,
        hasValidLat: Boolean(
          freshLat && freshLat !== "null" && freshLat.trim() !== ""
        ),
        hasValidLng: Boolean(
          freshLng && freshLng !== "null" && freshLng.trim() !== ""
        ),
        isControlNoOnlySearch: isControlNoOnlySearch,
        status: status,
        hasClaimsOnly: hasClaimsOnly, // Add this line
      });

      // Update component state with the fresh values
      setActiveFilterCarrier(freshCarrier);
      setActiveFilterControlNo(freshControlNo);
      setActiveFilterHasClaimsOnly(hasClaimsOnly); // Add this line

      // Add a delay to ensure all localStorage values are synchronized
      setTimeout(() => {
        // Force a fetch with the fresh values
        fetchCarrierBreakdown(true);
      }, 500);
    };

    // Add a handler for direct localStorage changes
    const handleStorageChange = (e) => {
      if (
        e.key === "currentLatitude" ||
        e.key === "currentLongitude" ||
        e.key === "currentFilterCarrier" ||
        e.key === "currentFilterStatus" ||
        e.key === "currentFilterControlNo" ||
        e.key === "currentFilterHasClaimsOnly" || // Add this line
        e.key === "radius" ||
        e.key === "currentZipcodes"
      ) {
        console.log(`Storage changed directly - ${e.key}:`, e.newValue);

        // When any important storage value changes, trigger a re-fetch after a short delay
        // to allow all related values to be updated
        setTimeout(() => {
          fetchCarrierBreakdown(true);
        }, 200);
      }
    };

    // (carrier breakdown precision): when the main component
    // emits an updated exclusion list, re-run the aggregation so sidebar
    // totals stay in sync with the list/map trimming.
    const handleExcludeControlNosUpdated = () => {
      console.log("Received exclude-controlnos-updated event");
      setTimeout(() => fetchCarrierBreakdown(true), 100);
    };

    // Add all event listeners
    window.addEventListener("geo-data-loading-start", handleDataLoadingStart);
    window.addEventListener(
      "geo-data-loading-complete",
      handleDataLoadingComplete
    );
    window.addEventListener("geo-data-loading-failed", handleDataLoadingFailed);
    window.addEventListener("geo-data-clearing", handleDataClearing);
    window.addEventListener("geo-data-cleared", handleDataCleared);
    window.addEventListener("markers-fully-loaded", handleMarkersLoaded);
    window.addEventListener(
      "exclude-controlnos-updated",
      handleExcludeControlNosUpdated
    );
    window.addEventListener("storage", handleStorageChange);

    // Cleanup event listeners
    return () => {
      window.removeEventListener(
        "geo-data-loading-start",
        handleDataLoadingStart
      );
      window.removeEventListener(
        "geo-data-loading-complete",
        handleDataLoadingComplete
      );
      window.removeEventListener(
        "geo-data-loading-failed",
        handleDataLoadingFailed
      );
      window.removeEventListener("geo-data-clearing", handleDataClearing);
      window.removeEventListener("geo-data-cleared", handleDataCleared);
      window.removeEventListener("markers-fully-loaded", handleMarkersLoaded);
      window.removeEventListener(
        "exclude-controlnos-updated",
        handleExcludeControlNosUpdated
      );
      window.removeEventListener("storage", handleStorageChange);
      // Abort any in-flight carrier-breakdown fetch on unmount so the retry loop doesn't keep running after the sidebar is gone.
      if (carrierAbortController.current) {
        carrierAbortController.current.abort();
        carrierAbortController.current = null;
      }
    };
  }, []);

  // Format number as currency
  const formatCurrency = (value) => {
    if (!value) return "$0";

    // Convert string to number if needed
    const numValue = typeof value === "string" ? parseFloat(value) : value;

    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numValue);
  };

  // Clear all active filters and refetch data
  const clearFilters = () => {
    console.log("Clearing all filters");

    // Remove from localStorage
    localStorage.removeItem("currentFilterCarrier");
    localStorage.removeItem("currentFilterControlNo");
    localStorage.removeItem("currentFilterHasClaimsOnly"); // Add this line
    localStorage.removeItem("currentLatitude");
    localStorage.removeItem("currentLongitude");
    localStorage.removeItem("radius");
    localStorage.removeItem("currentFilterStatus");

    // Update state
    setActiveFilterCarrier("");
    setActiveFilterControlNo("");
    setActiveFilterHasClaimsOnly(false); // Add this line

    // Dispatch event to sync with main component
    window.dispatchEvent(
      new CustomEvent("filter-params-changed", {
        detail: { carrier: "", controlNo: "", hasClaimsOnly: false },
      })
    );

    // Force refetch with cleared filters after a short delay
    setTimeout(() => fetchCarrierBreakdown(true), 100);
  };

  // Updated render function to show active filters
  const renderCarrierBreakdown = () => {
    if (mainComponentLoading) {
      return <div className="text-center my-4">Map data is loading...</div>;
    }

    if (loading) {
      return <div className="text-center my-4">Loading carrier data...</div>;
    }

    if (error) {
      return (
        <div className="text-center text-danger my-4">
          {error}
          <button
            className="btn btn-sm btn-outline-primary d-block mx-auto mt-2"
            onClick={() => fetchCarrierBreakdown(true)}
          >
            Retry
          </button>
        </div>
      );
    }

    if (!hasData) {
      return (
        <div className="text-center my-4 text-muted">
          Search for locations to view carrier data
        </div>
      );
    }

    const hasActiveFilters =
      activeFilterCarrier || activeFilterControlNo || activeFilterHasClaimsOnly;

    return (
      <>
        {hasActiveFilters && (
          <div className="active-filters mx-3 mb-3">
            <div className="d-flex justify-content-between align-items-center">
              <span className="fw-bold">Active Filters:</span>
              <button
                className="btn btn-sm btn-link text-secondary p-0"
                onClick={clearFilters}
              >
                Clear
              </button>
            </div>
            {activeFilterCarrier && (
              <div className="small text-secondary">
                Carrier: {activeFilterCarrier}
              </div>
            )}
            {activeFilterControlNo && (
              <div className="small text-secondary">
                Control No: {activeFilterControlNo}
              </div>
            )}
            {activeFilterHasClaimsOnly && (
              <div className="small text-secondary">Has Claims: Yes</div>
            )}
          </div>
        )}

        {carriers.map((carrier, index) => (
          <div className="card gc-card mx-3 mb-3" key={index}>
            <div className="card-header">{carrier.carrierName}</div>
            <div className="card-body">
              <p className="d-flex align-items-center justify-content-between mb-2">
                <strong>TIV</strong>
                {formatCurrency(carrier.totalTIV)}
              </p>
              <p className="d-flex align-items-center justify-content-between mb-0">
                <strong>Premium</strong>
                {formatCurrency(carrier.totalPremium)}
              </p>
              <p className="d-flex align-items-center justify-content-between mb-0">
                <strong>Claim</strong>
                {formatCurrency(carrier.totalIncurredClaims)}
              </p>
            </div>
          </div>
        ))}

        <ul className="gc-total mx-3">
          <li>
            <strong>Total TIV</strong>
            {formatCurrency(grandTotals.totalTIV)}
          </li>
          <li>
            <strong>Total Premium</strong>
            {formatCurrency(grandTotals.totalPremium)}
          </li>
          <li>
            <strong>Total Claim</strong>
            {formatCurrency(grandTotals.totalIncurredClaims)}
          </li>
        </ul>

        {zipcodesLastUpdated && (
          <div className="text-center text-muted small mt-2">
            Last updated: {zipcodesLastUpdated.toLocaleTimeString()}
          </div>
        )}
      </>
    );
  };

  return (
    <>
      <nav className={`gcSidebar sidebar ${isExpanded ? "expanded" : ""}`}>
        <div className="profile-area">
          <img src={ProfileImg} alt="Profile" className="profile-image" />
          <h2 className="profile-name">{userName}</h2>
        </div>
        <h5 className="text-center">Carrier Breakdown</h5>
        <div
          className={`carrierBreakdown ${hover ? "hovered" : ""}`}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
        >
          {renderCarrierBreakdown()}
        </div>
      </nav>
    </>
  );
};

export default GcSidebar;
